import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
interface MetricCardProps {
  name: string;
  value: string;
  change: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
  label: 'High' | 'Moderate' | 'Low';
  isVisible: boolean;
}
const getStatusColor = (name: string): string => {
  const colorMap: {
    [key: string]: string;
  } = {
    'Burnout Level': 'bg-teal-500',
    'Stress Index': 'bg-[#8679DF]',
    'Anxiety Index': 'bg-teal-500',
    'Psychological Safety': 'bg-teal-500',
    Engagement: 'bg-teal-500',
    'Morale Score': 'bg-[#8679DF]',
    'Social Connection': 'bg-teal-500'
  };
  return colorMap[name];
};
const getTextColor = (name: string): string => {
  const colorMap: {
    [key: string]: string;
  } = {
    'Burnout Level': 'text-teal-600',
    'Stress Index': 'text-[#8679DF]',
    'Anxiety Index': 'text-teal-600',
    'Psychological Safety': 'text-teal-600',
    Engagement: 'text-teal-600',
    'Morale Score': 'text-[#8679DF]',
    'Social Connection': 'text-teal-600'
  };
  return colorMap[name];
};
const getStatusLabel = (name: string): 'High' | 'Moderate' | 'Low' => {
  const labelMap: {
    [key: string]: 'High' | 'Moderate' | 'Low';
  } = {
    'Burnout Level': 'Low',
    'Stress Index': 'Moderate',
    'Anxiety Index': 'Low',
    'Psychological Safety': 'High',
    Engagement: 'High',
    'Morale Score': 'Moderate',
    'Social Connection': 'High'
  };
  return labelMap[name];
};
const MiniGauge = ({
  value,
  color
}: {
  value: number;
  color: string;
}) => <div className="h-1 w-16 bg-gray-200 rounded-full overflow-hidden" data-id="element-94">
    <motion.div initial={{
    width: 0
  }} animate={{
    width: `${value}%`
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className={`h-full ${color}`} data-id="element-95" />
  </div>;
export const MetricCard: React.FC<MetricCardProps> = ({
  name,
  value,
  change,
  isVisible
}) => {
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  const [displayValue, setDisplayValue] = useState(0);
  const statusLabel = getStatusLabel(name);
  const statusColor = getStatusColor(name);
  const textColor = getTextColor(name);
  useEffect(() => {
    if (isVisible) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / 600; // 0.6s duration
        if (progress < 1) {
          setDisplayValue(Math.min(Math.floor(numericValue * progress), numericValue));
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(numericValue);
        }
      };
      requestAnimationFrame(animate);
    } else {
      setDisplayValue(0);
    }
  }, [isVisible, numericValue]);
  return <AnimatePresence mode="wait" data-id="element-96">
      {isVisible && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="rounded-xl backdrop-blur-[16px] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.06)] p-4" data-id="element-97">
          <div className="flex justify-between items-start mb-2" data-id="element-98">
            <h3 className="text-sm font-medium text-gray-800" data-id="element-99">{name}</h3>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor} bg-opacity-20 ${textColor}`} data-id="element-100">
              {statusLabel}
            </span>
          </div>
          <div className="flex items-center justify-between" data-id="element-101">
            <div className="flex items-baseline gap-2" data-id="element-102">
              <div className="text-2xl font-bold text-gray-900" data-id="element-103">
                {displayValue}
              </div>
              {change.direction !== 'neutral' && <div className={`flex items-center text-sm ${textColor}`} data-id="element-104">
                  {change.direction === 'up' ? <TrendingUpIcon className="w-4 h-4 mr-0.5" data-id="element-105" /> : <TrendingDownIcon className="w-4 h-4 mr-0.5" data-id="element-106" />}
                  {change.value}
                </div>}
            </div>
            <MiniGauge value={numericValue} color={statusColor} data-id="element-107" />
          </div>
        </motion.div>}
    </AnimatePresence>;
};